import React, { useEffect, useState } from "react";
import { Download, Globe2, KeyRound, Lock, RefreshCcw, Trash2 } from "lucide-react";
import {
  requestAccountDeletion,
  requestDataExport,
  updateSubscription,
  changePassword,
} from "../../api/api";
import { useAuth } from "../../context/AuthContext";
import { useTier } from "../../hooks/useTier";
import { useUpsell } from "../paywall/UpsellProvider";
import { canUseMockBilling } from "../paywall/billingVisibility";
import { useLanguage } from "../../i18n/LanguageContext";

const ENABLE_MOCK_BILLING = process.env.REACT_APP_ENABLE_MOCK_BILLING === "true";
const AI_REGION_KEY = "reflecta.settings.aiRegion";

const readSetting = (key, fallback) => {
  if (typeof window === "undefined") return fallback;
  return window.localStorage.getItem(key) || fallback;
};

const AccountSettings = () => {
  const { user, refreshUser } = useAuth();
  const { tier, currentPeriodEnd, isPremium } = useTier();
  const upsell = useUpsell();
  const { language, setLanguage, t } = useLanguage();
  const showMockBillingControls = canUseMockBilling({
    enabled: ENABLE_MOCK_BILLING,
    user,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [aiRegion, setAiRegion] = useState(() => readSetting(AI_REGION_KEY, "US"));
  const [exportStatus, setExportStatus] = useState("");
  const [deleteStepOpen, setDeleteStepOpen] = useState(false);
  const [deletePhrase, setDeletePhrase] = useState("");
  const [deleteStatus, setDeleteStatus] = useState("");
  const [pwOpen, setPwOpen] = useState(false);
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwStatus, setPwStatus] = useState("");

  useEffect(() => {
    if (!isPremium && aiRegion !== "US") {
      setAiRegion("US");
      window.localStorage.setItem(AI_REGION_KEY, "US");
    }
  }, [aiRegion, isPremium]);

  const handleDowngrade = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await updateSubscription({ tier: "free" });
      await refreshUser();
    } catch (err) {
      setError(t("settings.changeFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleRegionChange = (value) => {
    if (value === "EU" && !isPremium) {
      upsell?.open(t("upsell.euRegionReason"));
      return;
    }
    setAiRegion(value);
    window.localStorage.setItem(AI_REGION_KEY, value);
  };

  const handleLanguageChange = (value) => {
    setLanguage(value);
  };

  const handleExport = async () => {
    setExportStatus(t("settings.requestingExport"));
    setError(null);
    try {
      const response = await requestDataExport();
      setExportStatus(t("settings.exportRequested", { id: response.request_id || "pending" }));
    } catch (err) {
      setExportStatus("");
      setError(t("settings.exportFailed"));
    }
  };

  const handleDeletion = async () => {
    if (deletePhrase !== "DELETE") {
      setError(t("settings.deletePhrase"));
      return;
    }
    setError(null);
    setDeleteStatus(t("settings.schedulingDeletion"));
    try {
      const response = await requestAccountDeletion();
      setDeleteStatus(
        t("settings.deletionScheduled", { date: new Date(response.scheduled_for).toLocaleDateString() })
      );
      setDeleteStepOpen(false);
      setDeletePhrase("");
    } catch (err) {
      setDeleteStatus("");
      setError(t("settings.deletionFailed"));
    }
  };

  const handlePasswordChange = async () => {
    if (pwNew.length < 8) {
      setError(t("settings.passwordShort"));
      return;
    }
    if (pwNew !== pwConfirm) {
      setError(t("settings.passwordMismatch"));
      return;
    }
    setError(null);
    setIsSaving(true);
    setPwStatus("");
    try {
      await changePassword({ oldPassword: pwCurrent, newPassword: pwNew });
      setPwStatus(t("settings.passwordChanged"));
      setPwCurrent("");
      setPwNew("");
      setPwConfirm("");
      setPwOpen(false);
    } catch (err) {
      setError(err.message || t("settings.passwordFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <section className="border-b border-gray-200 pb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t("settings.title")}</h1>
        <p className="mt-1 text-sm text-gray-500">{user?.email}</p>
      </section>

      <section className="space-y-4">
        <div className="flex flex-col gap-5 rounded-2xl border border-gray-100 bg-white p-6 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t("settings.subscription")}</h2>
            <dl className="mt-3 grid gap-2 text-sm text-gray-600 sm:grid-cols-2">
              <div>
                <dt className="font-medium text-gray-900">{t("settings.tier")}</dt>
                <dd>{tier === "premium" ? t("nav.premium") : t("nav.free")}</dd>
              </div>
              <div>
                <dt className="font-medium text-gray-900">{t("settings.source")}</dt>
                <dd>{showMockBillingControls ? t("settings.mock") : t("settings.account")}</dd>
              </div>
              {currentPeriodEnd && (
                <div className="sm:col-span-2">
                  <dt className="font-medium text-gray-900">{t("settings.currentPeriodEnd")}</dt>
                  <dd>{new Date(currentPeriodEnd).toLocaleString()}</dd>
                </div>
              )}
            </dl>
          </div>

          {tier === "premium" ? (
            showMockBillingControls && (
              <button
                type="button"
                onClick={handleDowngrade}
                disabled={isSaving}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-800 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9B30FF] focus-visible:ring-offset-2"
              >
                <RefreshCcw className="h-4 w-4" />
                {t("settings.downgrade")}
              </button>
            )
          ) : (
            <button
              type="button"
              onClick={() => upsell?.open(t("common.premium"))}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#9B30FF] to-[#6A0DAD] px-5 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-purple-500/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9B30FF] focus-visible:ring-offset-2"
            >
              <Lock className="h-4 w-4" />
              {t("common.upgrade")}
            </button>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <section className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-2">
              <Globe2 className="h-5 w-5 text-purple-600" />
              <h2 className="text-lg font-semibold text-gray-900">{t("settings.aiRegion")}</h2>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              {[
                ["US", "US"],
                ["EU", "EU"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => handleRegionChange(value)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                    aiRegion === value
                      ? "bg-purple-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  } ${value === "EU" && !isPremium ? "opacity-60" : ""}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </section>

          <section className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900">{t("settings.language")}</h2>
            <div className="mt-4 grid grid-cols-2 gap-2">
              {[
                ["de", "Deutsch"],
                ["en", "English"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => handleLanguageChange(value)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                    language === value
                      ? "bg-purple-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </section>
        </div>

        <section className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <KeyRound className="h-5 w-5 text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-900">{t("settings.password")}</h2>
              </div>
              {pwStatus && <p className="mt-1 text-sm text-green-600">{pwStatus}</p>}
            </div>
            {!pwOpen && (
              <button
                type="button"
                onClick={() => setPwOpen(true)}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50"
              >
                <KeyRound className="h-4 w-4" />
                {t("settings.password")}
              </button>
            )}
          </div>
          {pwOpen && (
            <div className="mt-4 max-w-sm space-y-3">
              <input
                type="password"
                value={pwCurrent}
                onChange={(e) => setPwCurrent(e.target.value)}
                placeholder={t("settings.currentPassword")}
                className="input-field"
              />
              <input
                type="password"
                value={pwNew}
                onChange={(e) => setPwNew(e.target.value)}
                placeholder={t("settings.newPassword")}
                className="input-field"
              />
              <input
                type="password"
                value={pwConfirm}
                onChange={(e) => setPwConfirm(e.target.value)}
                placeholder={t("settings.confirmPassword")}
                className="input-field"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handlePasswordChange}
                  disabled={isSaving}
                  className="rounded-full bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-700 disabled:opacity-60"
                >
                  {t("common.save")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPwOpen(false);
                    setPwCurrent("");
                    setPwNew("");
                    setPwConfirm("");
                    setError(null);
                  }}
                  className="rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  {t("common.cancel")}
                </button>
              </div>
            </div>
          )}
        </section>

        <section className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{t("settings.dataExport")}</h2>
              {exportStatus && <p className="mt-1 text-sm text-gray-500">{exportStatus}</p>}
            </div>
            <button
              type="button"
              onClick={handleExport}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50"
            >
              <Download className="h-4 w-4" />
              {t("settings.requestExport")}
            </button>
          </div>
        </section>

        <section className="rounded-2xl border border-red-100 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-red-700">{t("settings.deleteAccount")}</h2>
              {deleteStatus && <p className="mt-1 text-sm text-gray-500">{deleteStatus}</p>}
            </div>
            {!deleteStepOpen ? (
              <button
                type="button"
                onClick={() => setDeleteStepOpen(true)}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-red-50 px-5 py-2.5 text-sm font-semibold text-red-700 hover:bg-red-100"
              >
                <Trash2 className="h-4 w-4" />
                {t("settings.delete")}
              </button>
            ) : (
              <div className="w-full max-w-sm space-y-3">
                <input
                  type="text"
                  value={deletePhrase}
                  onChange={(event) => setDeletePhrase(event.target.value)}
                  placeholder={t("settings.typeDelete")}
                  className="input-field"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleDeletion}
                    className="rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
                  >
                    {t("settings.confirmDeletion")}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setDeleteStepOpen(false);
                      setDeletePhrase("");
                    }}
                    className="rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    {t("common.cancel")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        {error && <p className="text-sm text-red-600">{error}</p>}
      </section>
    </div>
  );
};

export default AccountSettings;
